import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  formatOrderCurrency,
  getCurrentMonthLabel,
  type CourseRevenueRow,
  type OrderAnalytics,
} from '../../utils/orderAnalytics';

interface OrdersAnalyticsProps {
  analytics: OrderAnalytics;
  isLoading: boolean;
  hasActiveFilters: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  مكتمل: '#34d399',
  'قيد الانتظار': '#fbbf24',
  ملغي: '#f87171',
  مسترد: '#c084fc',
};

const CHART_TOOLTIP_STYLE = {
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
  boxShadow: '0 10px 25px rgba(15, 23, 42, 0.08)',
  fontSize: '13px',
};

const RANK_BADGE_CLASS: Record<number, string> = {
  1: 'bg-gradient-to-br from-amber-400 to-orange-500 text-white ring-4 ring-amber-100',
  2: 'bg-gradient-to-br from-slate-300 to-slate-500 text-white ring-4 ring-slate-100',
  3: 'bg-gradient-to-br from-orange-300 to-amber-600 text-white ring-4 ring-orange-100',
};

const getRankBadgeClass = (rank: number) =>
  RANK_BADGE_CLASS[rank] || 'bg-white text-indigo-700 border-2 border-indigo-100';

interface TopCoursesRevenueListProps {
  courses: CourseRevenueRow[];
  totalRevenue: number;
}

const TopCoursesRevenueList = ({ courses, totalRevenue }: TopCoursesRevenueListProps) => {
  const maxRevenue = courses[0]?.revenue || 1;
  const topCoursesRevenue = courses.reduce((sum, course) => sum + course.revenue, 0);
  const topCoursesShare = totalRevenue > 0 ? Math.round((topCoursesRevenue / totalRevenue) * 100) : 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-gradient-to-l from-indigo-50 via-violet-50 to-white px-4 py-3 border border-indigo-100/80">
        <div>
          <p className="text-xs text-indigo-600 font-medium">إجمالي إيرادات أفضل 8 دورات</p>
          <p className="text-xl font-bold text-gray-900 mt-0.5">{formatOrderCurrency(topCoursesRevenue)}</p>
        </div>
        <div className="text-left">
          <p className="text-xs text-gray-500">نسبة من إجمالي الإيرادات</p>
          <p className="text-lg font-semibold text-indigo-600">{topCoursesShare}%</p>
        </div>
      </div>

      <div className="space-y-3 max-h-[340px] overflow-y-auto pr-1">
        {courses.map((course, index) => {
          const rank = index + 1;
          const widthPercent = Math.max(8, Math.round((course.revenue / maxRevenue) * 100));
          const revenueShare = totalRevenue > 0 ? Math.round((course.revenue / totalRevenue) * 100) : 0;

          return (
            <div
              key={course.name}
              className="group rounded-xl border border-gray-100 bg-gray-50/60 p-4 transition-all duration-200 hover:border-indigo-100 hover:bg-white hover:shadow-sm"
            >
              <div className="flex items-start gap-3">
                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-sm font-bold ${getRankBadgeClass(rank)}`}
                >
                  {rank}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                    <h4 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2">
                      {course.name}
                    </h4>
                    <div className="shrink-0 sm:min-w-[120px]">
                      <p className="text-base font-bold text-indigo-600">{formatOrderCurrency(course.revenue)}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {course.orders.toLocaleString('ar-EG')} طلب • {revenueShare}%
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-gray-200/80">
                    <div
                      className="h-full rounded-full bg-gradient-to-l from-indigo-500 via-violet-500 to-purple-500 transition-all duration-500 ease-out"
                      style={{ width: `${widthPercent}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface HeroRevenueCardProps {
  title: string;
  value: string;
  subtitle: string;
  badge: string;
  gradient: string;
  glow: string;
  icon: React.ReactNode;
}

const HeroRevenueCard = ({
  title,
  value,
  subtitle,
  badge,
  gradient,
  glow,
  icon,
}: HeroRevenueCardProps) => (
  <div
    className={`relative overflow-hidden rounded-2xl p-6 text-white shadow-lg ${gradient}`}
  >
    <div className={`absolute -top-10 -left-10 h-32 w-32 rounded-full blur-3xl opacity-60 ${glow}`} />
    <div className="absolute top-0 left-0 h-full w-full bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.22),transparent_55%)]" />
    <div className="relative flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="mb-3 inline-flex items-center rounded-full bg-white/15 px-3 py-1 text-xs font-medium backdrop-blur-sm">
          {badge}
        </div>
        <p className="text-sm text-white/80 mb-1">{title}</p>
        <p className="text-3xl sm:text-4xl font-bold tracking-tight break-words">{value}</p>
        <p className="mt-2 text-sm text-white/75">{subtitle}</p>
      </div>
      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm">
        {icon}
      </div>
    </div>
  </div>
);

interface MetricCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  valueClass: string;
  iconWrapClass: string;
}

const MetricCard = ({ title, value, subtitle, icon, valueClass, iconWrapClass }: MetricCardProps) => (
  <div className="group rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm text-gray-500 mb-1">{title}</p>
        <p className={`text-2xl font-bold ${valueClass}`}>{value}</p>
        {subtitle && <p className="mt-1 text-xs text-gray-400">{subtitle}</p>}
      </div>
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${iconWrapClass}`}>
        {icon}
      </div>
    </div>
  </div>
);

const StatusPill = ({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) => (
  <div className="flex items-center justify-between rounded-xl border border-gray-100 bg-white px-4 py-3 shadow-sm">
    <div className="flex items-center gap-2">
      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
      <span className="text-sm text-gray-600">{label}</span>
    </div>
    <span className="text-lg font-semibold text-gray-900">{value.toLocaleString('ar-EG')}</span>
  </div>
);

const ChartCard = ({
  title,
  description,
  children,
  emptyMessage,
  isEmpty,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  emptyMessage: string;
  isEmpty: boolean;
}) => (
  <div className="rounded-2xl border border-gray-100 bg-white p-5 sm:p-6 shadow-sm">
    <div className="mb-5">
      <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
    </div>
    {isEmpty ? (
      <div className="h-64 flex items-center justify-center rounded-xl bg-gray-50 text-sm text-gray-500">
        {emptyMessage}
      </div>
    ) : (
      children
    )}
  </div>
);

const AnalyticsSkeleton = () => (
  <div className="space-y-6 animate-pulse">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="h-40 rounded-2xl bg-gray-200" />
      <div className="h-40 rounded-2xl bg-gray-200" />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="h-28 rounded-2xl bg-gray-200" />
      ))}
    </div>
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <div className="h-80 rounded-2xl bg-gray-200" />
      <div className="h-80 rounded-2xl bg-gray-200" />
    </div>
  </div>
);

const OrdersAnalytics: React.FC<OrdersAnalyticsProps> = ({
  analytics,
  isLoading,
  hasActiveFilters,
}) => {
  if (isLoading) {
    return <AnalyticsSkeleton />;
  }

  if (analytics.totalOrders === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-12 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-500">
          <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-800">لا توجد بيانات للتحليل</h3>
        <p className="mt-2 text-sm text-gray-500">جرّب تعديل الفلاتر أو العودة لاحقاً عند توفر طلبات جديدة.</p>
      </div>
    );
  }

  const courseChartData = analytics.revenueByCourse;

  const todayLabel = new Date().toLocaleDateString('ar-EG', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <div className="space-y-6">
      {hasActiveFilters && (
        <div className="rounded-2xl border border-indigo-100 bg-gradient-to-l from-indigo-50 to-white px-4 py-3 text-sm text-indigo-800">
          التحليلات تعكس الفلاتر المطبقة حالياً على الطلبات.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <HeroRevenueCard
          title="إيرادات اليوم"
          value={formatOrderCurrency(analytics.todayRevenue)}
          subtitle={`${analytics.todayCompletedOrders.toLocaleString('ar-EG')} طلب مكتمل • ${todayLabel}`}
          badge="اليوم"
          gradient="bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600"
          glow="bg-emerald-300"
          icon={
            <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          }
        />
        <HeroRevenueCard
          title="إيرادات الشهر"
          value={formatOrderCurrency(analytics.monthRevenue)}
          subtitle={`${analytics.monthCompletedOrders.toLocaleString('ar-EG')} طلب مكتمل • ${getCurrentMonthLabel()}`}
          badge="هذا الشهر"
          gradient="bg-gradient-to-br from-indigo-500 via-violet-500 to-purple-600"
          glow="bg-violet-300"
          icon={
            <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          }
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <MetricCard
          title="إجمالي الطلبات"
          value={analytics.totalOrders.toLocaleString('ar-EG')}
          icon={
            <svg className="h-5 w-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          }
          valueClass="text-indigo-600"
          iconWrapClass="bg-indigo-50"
        />
        <MetricCard
          title="إجمالي الإيرادات"
          value={formatOrderCurrency(analytics.totalRevenue)}
          subtitle="للطلبات المكتملة"
          icon={
            <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          valueClass="text-green-600"
          iconWrapClass="bg-green-50"
        />
        <MetricCard
          title="إجمالي الخصومات"
          value={formatOrderCurrency(analytics.totalDiscounts)}
          icon={
            <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
            </svg>
          }
          valueClass="text-amber-600"
          iconWrapClass="bg-amber-50"
        />
        <MetricCard
          title="متوسط قيمة الطلب"
          value={formatOrderCurrency(analytics.averageOrderValue)}
          subtitle="للطلبات المكتملة"
          icon={
            <svg className="h-5 w-5 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          }
          valueClass="text-sky-600"
          iconWrapClass="bg-sky-50"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatusPill label="طلبات مكتملة" value={analytics.completedCount} color={STATUS_COLORS['مكتمل']} />
        <StatusPill label="قيد الانتظار" value={analytics.pendingCount} color={STATUS_COLORS['قيد الانتظار']} />
        <StatusPill label="طلبات ملغاة" value={analytics.cancelledCount} color={STATUS_COLORS['ملغي']} />
        <StatusPill label="طلبات مستردة" value={analytics.refundedCount} color={STATUS_COLORS['مسترد']} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ChartCard
          title="توزيع حالات الطلبات"
          description="نظرة سريعة على حالة كل طلب"
          isEmpty={analytics.statusDistribution.length === 0}
          emptyMessage="لا توجد بيانات للحالات"
        >
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={analytics.statusDistribution}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={68}
                outerRadius={102}
                paddingAngle={4}
                stroke="none"
              >
                {analytics.statusDistribution.map((entry) => (
                  <Cell key={entry.status} fill={STATUS_COLORS[entry.name] || '#94a3b8'} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={CHART_TOOLTIP_STYLE}
                formatter={(value: number) => [value.toLocaleString('ar-EG'), 'العدد']}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="أعلى الدورات إيراداً"
          description="ترتيب أفضل 8 دورات حسب الإيرادات المكتملة"
          isEmpty={courseChartData.length === 0}
          emptyMessage="لا توجد إيرادات للدورات"
        >
          <TopCoursesRevenueList courses={courseChartData} totalRevenue={analytics.totalRevenue} />
        </ChartCard>
      </div>

      <ChartCard
        title="الطلبات والإيرادات عبر الزمن"
        description="متابعة الأداء اليومي للطلبات والإيرادات"
        isEmpty={analytics.ordersOverTime.length === 0}
        emptyMessage="لا توجد بيانات زمنية"
      >
        <ResponsiveContainer width="100%" height={340}>
          <LineChart data={analytics.ordersOverTime} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 12 }} interval="preserveStartEnd" />
            <YAxis yAxisId="orders" allowDecimals={false} orientation="right" tick={{ fill: '#64748b', fontSize: 12 }} />
            <YAxis
              yAxisId="revenue"
              orientation="left"
              tickFormatter={(value) => `${Math.round(value / 1000)}k`}
              tick={{ fill: '#64748b', fontSize: 12 }}
            />
            <Tooltip
              contentStyle={CHART_TOOLTIP_STYLE}
              formatter={(value: number, name: string) => {
                if (name === 'الإيراد') return [formatOrderCurrency(value), 'الإيراد'];
                return [value, 'الطلبات'];
              }}
              labelFormatter={(_, payload) => {
                const date = payload?.[0]?.payload?.date;
                return date
                  ? new Date(date).toLocaleDateString('ar-EG', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })
                  : '';
              }}
            />
            <Legend />
            <Line
              yAxisId="orders"
              type="monotone"
              dataKey="orders"
              name="الطلبات"
              stroke="#0ea5e9"
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 5 }}
            />
            <Line
              yAxisId="revenue"
              type="monotone"
              dataKey="revenue"
              name="الإيراد"
              stroke="#22c55e"
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ChartCard
          title="عدد الطلبات حسب اليوم"
          description="حجم الطلبات اليومي"
          isEmpty={analytics.ordersOverTime.length === 0}
          emptyMessage="لا توجد بيانات يومية"
        >
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analytics.ordersOverTime}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} tick={{ fill: '#64748b', fontSize: 12 }} />
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(value: number) => [value, 'الطلبات']} />
              <Bar dataKey="orders" fill="#4f46e5" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="أكثر أكواد الخصم استخداماً"
          description="الأكواد الأكثر تفعيلاً في الطلبات"
          isEmpty={analytics.topPromoCodes.length === 0}
          emptyMessage="لا توجد أكواد خصم مستخدمة"
        >
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analytics.topPromoCodes}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="code" tick={{ fill: '#64748b', fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fill: '#64748b', fontSize: 12 }} />
              <Tooltip
                contentStyle={CHART_TOOLTIP_STYLE}
                formatter={(value: number, name: string) => {
                  if (name === 'discount') return [formatOrderCurrency(value), 'إجمالي الخصم'];
                  return [value, 'مرات الاستخدام'];
                }}
              />
              <Legend />
              <Bar dataKey="uses" name="uses" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
};

export default OrdersAnalytics;
